import React from 'react';
import { Card } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { FiChevronRight } from 'react-icons/fi';
import type { DashboardWorkQueue, WorkQueueGroupKey, WorkQueueMetricKey } from '../types/dashboard';
import {
  WORK_QUEUE_GROUP_CONFIG,
  WORK_QUEUE_METRIC_LABELS,
  WORK_QUEUE_METRIC_PATH_SUFFIX,
} from '../types/dashboard';

interface Props {
  workQueue: DashboardWorkQueue;
  scopeSegment: string;
  scopeId: number;
}

const metricColorMap: Record<WorkQueueMetricKey, 'primary' | 'warning' | 'info'> = {
  draft: 'primary',
  needs_attention: 'warning',
  awaiting_my_approval: 'info',
};

const GROUP_ORDER: WorkQueueGroupKey[] = [
  'activity',
  'department_reports',
  // Temporarily hidden: Leadership Reports
  // 'pastor_report',
  // 'finance_statistics',
];

/** "My work": one row per work-queue group with chip links for counts above 0. */
const WorkQueueSection: React.FC<Props> = ({ workQueue, scopeSegment, scopeId }) => {
  const navigate = useNavigate();
  const basePath = `/${scopeSegment}/${scopeId}`;

  const groups = GROUP_ORDER.filter((key) => workQueue[key] !== undefined);

  if (!groups.length) return null;

  return (
    <Card className="ntcog-card dashboard-section-card dashboard-my-work-card">
      <Card.Header>
        <div>My work</div>
        <small className="text-muted fw-normal d-block">Across all open reporting periods</small>
      </Card.Header>
      <Card.Body className="p-0">
        <ul className="list-unstyled mb-0">
          {groups.map((groupKey) => {
            const group = workQueue[groupKey]!;
            const config = WORK_QUEUE_GROUP_CONFIG[groupKey];
            const metrics = config.metrics.filter(
              (metricKey) => typeof group[metricKey] === 'number'
            );
            const total = metrics.reduce(
              (sum, metricKey) => sum + (group[metricKey] as number),
              0
            );

            return (
              <li key={groupKey} className="dashboard-my-work-row">
                <div className="dashboard-my-work-row__title">
                  <span>{config.title}</span>
                  <span className="dashboard-my-work-row__total text-muted">{total}</span>
                </div>
                <div className="dashboard-my-work-row__chips">
                  {total === 0 ? (
                    <span className="text-muted small">Nothing waiting</span>
                  ) : (
                    metrics.map((metricKey) => {
                      const value = group[metricKey] as number;
                      if (value === 0) return null;
                      const path = `${basePath}${config.pathPrefix}${WORK_QUEUE_METRIC_PATH_SUFFIX[metricKey]}`;
                      return (
                        <a
                          key={metricKey}
                          href={path}
                          className={`dashboard-chip dashboard-chip--${metricColorMap[metricKey]}`}
                          onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                            e.preventDefault();
                            navigate(path);
                          }}
                        >
                          <span>
                            {value} {WORK_QUEUE_METRIC_LABELS[metricKey]}
                          </span>
                          <FiChevronRight size={14} aria-hidden />
                        </a>
                      );
                    })
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </Card.Body>
    </Card>
  );
};

export default WorkQueueSection;
